import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { makeGetSortedActiveProjectMilestones, makeGetSortedActiveProjectLists } from 'selectors/milestones';

import { showModal } from 'actions/modal';

import { useCanUser } from 'shared-hooks/permission';

import Popout from 'components/popout';
import MilestoneSelector from 'components/MilestoneSelector';
import ProjectSelector from 'components/ProjectSelector';
import ProjectName from 'components/project-name';
import MilestoneName from 'components/milestone-name';

import IconClose from 'icons/icon-close';
import IconArrowDown from 'icons/icon-arrow-down';

const ProjectNameWithWrapper = ({ id }) => (
    <span className="with-icon-after"><ProjectName id={id} /></span>
)

const SelectMilestonePopout = ({ selected, projectId, selectClassName = '', xAlign = 'right', defaultXPosition = 'left', inline, withHead, title, withProject = true, canSelectSame = true, itemComponent, withoutSelectTrigger, showCreate, isList, excluded, autoSelectFirst, firstListId, removeCopy, onShow, onHide, onSelect, onRemove, children, provideController }) => {
    const { t } = useTranslation(['milestone', 'common']);
    const [visible, setVisible] = useState(false);
    const [selectingProject, setSelectingProject] = useState(!projectId);
    const [projectIdState, setProjectIdState] = useState(projectId);
    const [selectedState, setSelectedState] = useState(selected);
    const [userSelected, setUserSelected] = useState(false);
    const popoutCtrl = useRef(null);
    const canCreate = useCanUser('createMilestone');

    const show = useCallback(() => {
        popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const hide = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handlePopoutOpenClick = useCallback(() => {
        show();
    }, [show]);

    const handlePopoutCloseClick = useCallback(() => {
        hide();
    }, [hide]);

    const selectElement = useMemo(() => {
        if (withoutSelectTrigger) {
            return null;
        }

        if (children) {
            return React.Children.map(children, child => 
                React.cloneElement(child, {
                    className: (child.props.className || '') + (visible ? ' selected disable-tooltip' : '') + (userSelected ? ' user-selected' : ''),
                    onClick: handlePopoutOpenClick
                })
            );
        }

        return (
            <div onClick={handlePopoutOpenClick} className={'resource-selector-select ' + selectClassName + (visible ? ' selected disable-tooltip' : '') + (userSelected ? ' user-selected' : '')}>
                {
                    selectedState ? (
                        <MilestoneName id={selectedState} />
                    ) : (
                        <span className="placeholder">{ t('selectList') }...</span>
                    )
                }

                <IconArrowDown />
            </div>
        )
    }, [withoutSelectTrigger, children, selectClassName, selectedState, visible, userSelected, handlePopoutOpenClick, t]);

    const handlePopoutShow = useCallback(() => {
        setVisible(true);

        onShow && onShow();
    }, [onShow]);

    const handlePopoutHide = useCallback(() => {
        setVisible(true);

        onHide && onHide()
    }, [onHide]);

    const handleMilestoneSelect = useCallback(id => {
        setSelectedState(id);
        setUserSelected(true);

        if (onSelect) {
            onSelect({
                id,
                projectId: projectIdState
            });

            hide();
        }
    }, [onSelect, projectIdState, hide]);

    const handleProjectChangeClick = useCallback(() => {
        setSelectingProject(true);
    }, []);

    const handleProjectSelect = useCallback(projectId => {
        setProjectIdState(projectId);
        setSelectingProject(false);
        setSelectedState(null);
    }, []);

    const handleMilestoneRemove = () => {
        onRemove?.();

        hide();
    }

    const handleCreateClick = useCallback(() => {
        showModal('MILESTONE_CREATE', {
            projectId: projectIdState,
            isList,
            onCreate: id => {
                setUserSelected(true);

                onSelect({
                    id,
                    projectId: projectIdState
                });
            }
        });

        hide();
    }, [projectIdState, onSelect, hide]);

    useEffect(() => {
        const ctrl = {
            show: () => show(),
            hide: () => hide()
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [provideController]);

    useEffect(() => {
        if (projectId && autoSelectFirst && !selected && firstListId) {
            setSelectedState(firstListId);

            onSelect({
                id: firstListId,
                projectId: projectIdState
            });
        }
    }, [autoSelectFirst, projectId]);

    return (
        <React.Fragment>
            { selectElement }

            <Popout
                className="no-arrow xxsmall popout-resource-selector"
                provideController={popoutCtrl}
                onShow={handlePopoutShow}
                onHide={handlePopoutHide}
                xAlign={xAlign}
                defaultXPosition={defaultXPosition}
                closeOnEveryClick={true}
                inline={inline}
            >
                {
                    withHead && title ? (
                        <div className="popout-head">
                            { title }

                            <button className="btn btn-icon btn-small popout-close" onClick={handlePopoutCloseClick}>
                                <IconClose />
                            </button>
                        </div>
                    ) : null
                }

                <div className="popout-main">
                    {
                        selectingProject ? (
                            <ProjectSelector selected={projectIdState} onSelect={handleProjectSelect} />
                        ) : (
                            <>
                                {
                                    withProject ? (
                                        <div className="popout-resource-selector-extra">
                                            <Trans
                                                i18nKey="project:In Project"
                                                components={{
                                                    strong: <strong className="clickable text-colored" onClick={handleProjectChangeClick} />,
                                                    ProjectName: <ProjectNameWithWrapper id={projectIdState} />,
                                                    IconArrowDown: <IconArrowDown size="14px" />
                                                }}
                                            />
                                        </div>
                                    ) : null
                                }

                                <MilestoneSelector
                                    projectId={projectIdState}
                                    selected={selectedState}
                                    onSelect={handleMilestoneSelect}
                                    excluded={excluded}
                                    isList={isList}
                                    autoSelectFirst={autoSelectFirst}
                                    resourceCreateText={ isList ? t('Create a list') : t('Create a milestone') }
                                    onResourceCreate={showCreate && canCreate && handleCreateClick}
                                />
                            </>
                        )
                    }
                </div>

                {
                    selectedState && onRemove ? (
                        <div className="popout-foot">
                            <span className="action" onClick={handleMilestoneRemove}>{removeCopy ?? t('Remove Milestone')}</span>
                        </div>
                    ) : null
                }
            </Popout>
        </React.Fragment>
    );
}

const makeMapStateToProps = () => {
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedActiveProjectLists = makeGetSortedActiveProjectLists();

    return ({ milestones }, ownProps) => {
        const lists = ownProps.isList ? getSortedActiveProjectLists(milestones.items, ownProps.projectId) : getSortedActiveProjectMilestones(milestones.items, ownProps.projectId);

        return {
            firstListId: lists?.[0]?.id
        }
    }
}

export default connect(makeMapStateToProps)(SelectMilestonePopout);
